import { useEffect, useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

/**
 * GiteaOrgPickerComponent
 *
 * Loads the list of Gitea organizations at render time via the Backstage
 * proxy (/api/proxy/gitea/admin/orgs) and presents them as a dropdown.
 *
 * Falls back to a free-form text input if the proxy call fails or returns
 * an empty list (e.g. during local dev without Gitea connectivity).
 */
const GiteaOrgPickerComponent = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<string>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [orgs, setOrgs] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const proxyBase = await discoveryApi.getBaseUrl('proxy');
        const res = await fetchApi.fetch(`${proxyBase}/gitea/user/orgs?limit=50`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: any[] = await res.json();
        if (!cancelled) {
          setOrgs(data.map(o => (o.username ?? o.name ?? '')).filter(Boolean));
        }
      } catch (e: any) {
        if (!cancelled) setFetchError(String(e?.message ?? e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <CircularProgress size={24} />;
  }

  // Graceful fallback: free-form text input when orgs can't be fetched
  if (fetchError || orgs.length === 0) {
    return (
      <TextField
        label="Gitea Organization"
        required={required}
        value={formData ?? ''}
        onChange={e => onChange(e.target.value)}
        error={(rawErrors?.length ?? 0) > 0}
        helperText={
          fetchError
            ? `Could not load orgs (${fetchError}) — enter org name manually`
            : 'Enter the Gitea organization name'
        }
        fullWidth
      />
    );
  }

  return (
    <FormControl fullWidth required={required} error={(rawErrors?.length ?? 0) > 0}>
      <InputLabel>Gitea Organization</InputLabel>
      <Select
        value={formData ?? ''}
        onChange={e => onChange(String(e.target.value))}
      >
        {orgs.map(org => (
          <MenuItem key={org} value={org}>
            {org}
          </MenuItem>
        ))}
      </Select>
      {(rawErrors?.length ?? 0) > 0 && (
        <FormHelperText>{rawErrors![0]}</FormHelperText>
      )}
    </FormControl>
  );
};

export const GiteaOrgPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GiteaOrgPicker',
    component: GiteaOrgPickerComponent,
  }),
);
